import { useEffect, useState } from "react";
import { useCustom } from "@refinedev/core";
import { PawPrint } from "lucide-react";

import { getAccessToken } from "../../auth";
import type { Inhoud } from "./types";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

/**
 * Een dier dat de klant via het portaal aanmeldt (DIER_NIEUW), leesbaar voor wie het beoordeelt: soort, ras en kleur
 * met hun naam in plaats van een id, de entingen als tabel en de foto die de klant meestuurde. Bij goedkeuren maakt
 * het pension het dier aan met precies deze gegevens, inclusief foto en entingen.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

interface Optie { id: number; label: string }
interface Bestand { id: string; soort: string; bestandsnaam: string; contentType: string }
interface Enting { naam?: string; merk?: string; datum?: string; geldigTot?: string }

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

function datum(iso: unknown, locale: string): string {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "—";
  return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "nl-NL", { day: "numeric", month: "long", year: "numeric" })
    .format(new Date(`${iso}T12:00:00`));
}

export function DierNieuwBlok({ t, locale, verzoekId, inhoud }: { t: T; locale: string; verzoekId: string; inhoud: Inhoud }) {
  const soortId = typeof inhoud.diersoortId === "number" ? inhoud.diersoortId : null;
  const soorten = useCustom<Optie[]>({ url: "animals/reference/species", method: "get" }).data?.data ?? [];
  const kleuren = useCustom<Optie[]>({ url: "animals/reference/colours", method: "get" }).data?.data ?? [];
  const rassen = useCustom<Optie[]>({
    url: `animals/reference/breeds${soortId ? `?species=${soortId}` : ""}`,
    method: "get",
    queryOptions: { enabled: soortId != null },
  }).data?.data ?? [];
  const bestanden = useCustom<Bestand[]>({ url: `klantverzoeken/${verzoekId}/bestanden`, method: "get" }).data?.data ?? [];
  const foto = Array.isArray(bestanden) ? bestanden.find((b) => b.soort === "DIERFOTO") : undefined;
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!foto) return;
    let url: string | null = null;
    let weg = false;
    const token = getAccessToken();
    fetch(`${API_URL}/klantverzoeken/${verzoekId}/bestanden/${foto.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (b && !weg) {
          url = URL.createObjectURL(b);
          setFotoUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      weg = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [foto, verzoekId]);

  const naam = (lijst: Optie[], id: unknown) =>
    typeof id === "number" ? (Array.isArray(lijst) ? lijst.find((o) => o.id === id)?.label : undefined) ?? `#${id}` : undefined;
  const soort = naam(soorten, inhoud.diersoortId) ?? (typeof inhoud.soort === "string" ? inhoud.soort : "—");
  const ras = naam(rassen, inhoud.rasId) ?? (typeof inhoud.ras === "string" ? inhoud.ras : "—");
  const kleur = naam(kleuren, inhoud.kleurId) ?? "—";
  const entingen = Array.isArray(inhoud.entingen) ? (inhoud.entingen as Enting[]) : [];
  const tekst = (v: unknown) => (typeof v === "string" && v.trim() ? v : "—");

  const regels: [string, string][] = [
    [t("klantverzoeken.dier.naam"), tekst(inhoud.naam)],
    [t("klantverzoeken.dier.soort"), soort],
    [t("klantverzoeken.dier.ras"), ras],
    [t("klantverzoeken.dier.geslacht"), tekst(inhoud.geslacht) + (inhoud.gecastreerd === true ? `, ${t("klantverzoeken.dier.gecastreerd")}` : "")],
    [t("klantverzoeken.dier.geboortedatum"), datum(inhoud.geboortedatum, locale)],
    [t("klantverzoeken.dier.chipnummer"), tekst(inhoud.chipnummer)],
    [t("klantverzoeken.dier.kleur"), kleur],
  ];

  return (
    <div className="kvd-dier">
      <div className="kvd-dier__kop">
        <div className="kvd-dier__foto">
          {fotoUrl ? <img src={fotoUrl} alt={t("klantverzoeken.dier.foto")} /> : <PawPrint size={32} aria-hidden="true" />}
        </div>
        <dl className="kvd-dier__gegevens">
          {regels.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>
      </div>

      <h3 className="kvd-dier__titel">{t("klantverzoeken.dier.entingen", { args: [entingen.length] })}</h3>
      {entingen.length === 0 ? <p className="kvd__stil">{t("klantverzoeken.dier.geenEntingen")}</p> : (
        <IndeelbareTabel
          tabelKey="klantverzoek-dier-entingen"
          columns={[
            { key: "enting", header: t("klantverzoeken.dier.enting"),
              cell: (e) => `${tekst(e.naam)}${e.merk ? ` (${e.merk})` : ""}` },
            { key: "toegediend", header: t("klantverzoeken.dier.toegediend"), cell: (e) => datum(e.datum, locale) },
            { key: "geldigTot", header: t("klantverzoeken.dier.geldigTot"), cell: (e) => datum(e.geldigTot, locale) },
          ]}
          rows={entingen}
          getRowKey={(e) => `${e.naam ?? ""}-${e.datum ?? ""}`}
          empty=""
        />
      )}

      {typeof inhoud.bijzonderheden === "string" && inhoud.bijzonderheden.trim() && (
        <>
          <h3 className="kvd-dier__titel">{t("klantverzoeken.dier.bijzonderheden")}</h3>
          <p className="kvd-dier__bijzonderheden">{inhoud.bijzonderheden}</p>
        </>
      )}
      <p className="kvd__stil">{t("klantverzoeken.dier.uitleg")}</p>
    </div>
  );
}
